import { useMemo, useState, useEffect, useCallback } from "react";
import { useApp } from "src/hooks/use-app";
import { apiUpdateSocialAccount } from "src/api";
import { notification } from "src/utils/notification";

interface UseTelegramBannerOptions {
  hasWatchlistOrAlert?: boolean; // Passed by calling page to indicate if user has watchlist or alert
}

export const useTelegramBanner = (options: UseTelegramBannerOptions = {}) => {
  const { userInfo, updateUserInfo } = useApp();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if user has linked Telegram
  const hasTelegramLinked = useMemo(() => {
    if (!userInfo?.social_accounts) {
      return false;
    }
    // return false;
    return userInfo.social_accounts.some((account) => account.type === "telegram");
  }, [userInfo?.social_accounts]);

  // Whether the banner should be shown
  const shouldShowBanner = useMemo(() => {
    // Basic conditions: user logged in, Telegram not linked, has subscriptions, not manually dismissed
    return Boolean(userInfo && !hasTelegramLinked && options.hasWatchlistOrAlert && !isDismissed);
  }, [userInfo, hasTelegramLinked, options.hasWatchlistOrAlert, isDismissed]);

  // Handle banner dismissal
  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Handle Link Now click - directly call Telegram binding API
  const handleLinkClick = useCallback(async () => {
    try {
      const { status, data } = await apiUpdateSocialAccount({
        type: "telegram",
      });

      if (data.data?.url) {
        // Open Telegram authorization popup
        let popup = window.open(data.data.url, "_blank");
        const interval = setInterval(() => {
          if (popup?.closed) {
            clearInterval(interval);
            updateUserInfo(); // Refresh user info
          }
        }, 1000);
      }

      if (status === 200 && data.code === 0) {
        updateUserInfo();
      } else if (data.message) {
        notification.error({ description: data.message });
      }
    } catch (err) {
      console.error("Failed to link Telegram:", err);
      // If API call fails, fallback to redirecting to profile page
      window.location.href = "/account/profile";
    }
  }, [updateUserInfo]);

  // Reset dismissal state when user info changes (e.g. user re-login)
  useEffect(() => {
    if (userInfo?.id) {
      // Reset state when user switches
      setIsDismissed(false);
    }
  }, [userInfo?.id]);

  useEffect(() => {
    setIsDismissed(false);
    // const dismissed = sessionStorage.getItem('telegram-banner-dismissed');
    // if (dismissed === 'true') {
    //   setIsDismissed(true);
    // }
  }, []);

  return {
    shouldShowBanner,
    hasTelegramLinked,
    handleDismiss,
    handleLinkClick,
  };
};
